/**
 * DatasourceDetailModal Component
 * View and edit datasource details with tabs
 */

import React, { useState } from 'react';
import { Modal } from '../shared/Modal';
import { StatusBadge } from '../shared/StatusBadge';
import { Datasource } from '../../types/platform.types';
import { maskCredentials } from '../../utils/security';
import { formatRelativeTime } from '../../utils/formatTime';
import { api } from '../../services/api';

interface DatasourceDetailModalProps {
  datasource: Datasource;
  onClose: () => void;
  onUpdate: () => void;
  onDelete: () => void;
}

type Tab = 'overview' | 'connection' | 'sync' | 'statistics';

const typeNames: Record<string, string> = {
  salesforce: 'Salesforce',
  sap_erp: 'SAP ERP',
  odoo: 'Odoo',
};

export const DatasourceDetailModal: React.FC<DatasourceDetailModalProps> = ({
  datasource,
  onClose,
  onUpdate,
  onDelete,
}) => {
  const [activeTab, setActiveTab] = useState<Tab>('overview');
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState<{
    type: 'success' | 'error';
    message: string;
    details?: { syncedModels?: any[]; skippedModels?: any[] };
  } | null>(null);

  const tabs: { key: Tab; label: string }[] = [
    { key: 'overview', label: 'Overview' },
    { key: 'connection', label: 'Connection' },
    { key: 'sync', label: 'Sync' },
    { key: 'statistics', label: 'Statistics' },
  ];

  return (
    <Modal isOpen={true} size="xl" onClose={onClose}>
      <div>
        {/* Header */}
        <div className="flex items-start justify-between mb-5">
          <div>
            <span className="eye">Source</span>
            <h2 style={{ fontSize: 21, fontWeight: 800, letterSpacing: '-.035em' }}>
              {datasource.name}
            </h2>
            <p className="dim" style={{ fontSize: 12.5, marginTop: 2 }}>
              {typeNames[datasource.type] || datasource.type}
            </p>
          </div>
          <StatusBadge status={datasource.status as any} size="md" />
        </div>

        {/* Tabs */}
        <div className="tabs" style={{ marginBottom: 16 }}>
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`tab${activeTab === tab.key ? ' on' : ''}`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div style={{ minHeight: 360 }}>
          {activeTab === 'overview' && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="lbl">Type</label>
                  <p style={{ fontWeight: 600 }}>{typeNames[datasource.type]}</p>
                </div>
                <div>
                  <label className="lbl">Status</label>
                  <div className="mt-1">
                    <StatusBadge status={datasource.status as any} size="sm" />
                  </div>
                </div>
                <div>
                  <label className="lbl">Created</label>
                  <p style={{ fontWeight: 600 }}>
                    {formatRelativeTime(datasource.created_at)}
                  </p>
                </div>
                <div>
                  <label className="lbl">Last Updated</label>
                  <p style={{ fontWeight: 600 }}>
                    {formatRelativeTime(datasource.updated_at)}
                  </p>
                </div>
              </div>

              {datasource.description && (
                <div>
                  <label className="lbl">Description</label>
                  <p style={{ fontWeight: 600 }}>{datasource.description}</p>
                </div>
              )}

              {datasource.last_sync_error && (
                <div className="errbox">
                  <div className="flex gap-2">
                    <svg className="w-5 h-5 text-red-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-red-900">Last Sync Error</p>
                      <p className="text-sm text-red-700 mt-1">{datasource.last_sync_error}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'connection' && (
            <div className="space-y-4">
              <div>
                <label className="lbl">Connection Type</label>
                <p style={{ fontWeight: 600 }}>{typeNames[datasource.type]}</p>
              </div>

              <div>
                <label className="lbl">
                  Credentials (Masked)
                </label>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  {datasource.encrypted_credentials ? (
                    <div className="space-y-2 font-mono text-sm">
                      {Object.entries(maskCredentials(datasource.encrypted_credentials as any)).map(
                        ([key, value]) => (
                          <div key={key} className="flex justify-between">
                            <span className="text-gray-600">{key}:</span>
                            <span className="text-gray-900">{value}</span>
                          </div>
                        )
                      )}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-sm">No credentials stored</p>
                  )}
                </div>
                <p className="hint">
                  Credentials are encrypted and cannot be viewed in plain text
                </p>
              </div>

              {datasource.connection_config && (
                <div>
                  <label className="lbl">
                    Connection Configuration
                  </label>
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <pre className="text-sm text-gray-900 whitespace-pre-wrap">
                      {JSON.stringify(datasource.connection_config, null, 2)}
                    </pre>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'sync' && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="lbl">Sync Enabled</label>
                  <p className="mt-1">
                    {datasource.sync_enabled ? (
                      <span className="text-green-600 font-medium">Yes</span>
                    ) : (
                      <span className="text-gray-600">No</span>
                    )}
                  </p>
                </div>
                {datasource.sync_enabled && (
                  <div>
                    <label className="lbl">Frequency</label>
                    <p style={{ fontWeight: 600 }}>
                      Every {datasource.sync_frequency_minutes} minutes
                    </p>
                  </div>
                )}
              </div>

              <div>
                <label className="lbl">Last Sync</label>
                <p style={{ fontWeight: 600 }}>
                  {datasource.last_sync_at
                    ? formatRelativeTime(datasource.last_sync_at)
                    : 'Never'}
                </p>
              </div>

              <div>
                <label className="lbl">Last Sync Status</label>
                <div className="mt-1">
                  {datasource.last_sync_status === 'success' && (
                    <StatusBadge status="success" size="sm" />
                  )}
                  {datasource.last_sync_status === 'error' && (
                    <StatusBadge status="error" size="sm" />
                  )}
                  {!datasource.last_sync_status && (
                    <span className="text-gray-500">No sync yet</span>
                  )}
                </div>
              </div>

              {datasource.allowed_roles && datasource.allowed_roles.length > 0 && (
                <div>
                  <label className="lbl">
                    Allowed Roles
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {datasource.allowed_roles.map((role) => (
                      <span
                        key={role}
                        className="tg tg-b"
                      >
                        {role}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Sync Now Button */}
              <div className="pt-4 border-t border-gray-200">
                <button
                  onClick={async () => {
                    setIsSyncing(true);
                    setSyncMessage(null);
                    try {
                      // Sync can take a long time, so we set a 25-minute timeout
                      const response = await api.post(`/datasources/${datasource.id}/sync`, {}, {
                        timeout: 25 * 60 * 1000, // 25 minutes
                      });

                      if (response.success) {
                        setSyncMessage({
                          type: 'success',
                          message: response.message || `Sync completed! Created ${response.documentsCreated || 0} documents.`,
                          details: {
                            syncedModels: response.syncedModels || [],
                            skippedModels: response.skippedModels || []
                          }
                        });
                        onUpdate(); // Refresh datasource data
                      } else {
                        setSyncMessage({
                          type: 'error',
                          message: response.message || 'Sync failed'
                        });
                      }
                    } catch (error: any) {
                      const errorMessage = error.response?.data?.error?.message || error.message || 'Failed to start sync';
                      setSyncMessage({ type: 'error', message: errorMessage });
                    } finally {
                      setIsSyncing(false);
                    }
                  }}
                  disabled={isSyncing}
                  className="w-full px-4 py-3 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-lg hover:from-cyan-600 hover:to-blue-600 transition-all font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isSyncing ? (
                    <>
                      <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Syncing...
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                      Sync Now
                    </>
                  )}
                </button>

                {syncMessage && (
                  <div className={`mt-3 p-4 rounded-lg ${ syncMessage.type === 'success' ? 'bg-green-50 border border-green-200 text-green-700' : 'bg-red-50 border border-red-200 text-red-700' }`}>
                    <p className="text-sm font-medium mb-2">{syncMessage.message}</p>

                    {syncMessage.type === 'success' && syncMessage.details && (
                      <div className="mt-3 space-y-2">
                        {syncMessage.details.syncedModels && syncMessage.details.syncedModels.length > 0 && (
                          <div>
                            <p className="text-xs font-semibold text-green-800 mb-1">✓ Synced Models:</p>
                            <div className="flex flex-wrap gap-1">
                              {syncMessage.details.syncedModels.map((item: any, idx: number) => (
                                <span key={idx} className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs">
                                  {item.model} ({item.count})
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        {syncMessage.details.skippedModels && syncMessage.details.skippedModels.length > 0 && (
                          <div>
                            <p className="text-xs font-semibold text-yellow-800 mb-1">⚠ Skipped Models:</p>
                            <div className="flex flex-wrap gap-1">
                              {syncMessage.details.skippedModels.map((item: any, idx: number) => (
                                <span key={idx} className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded text-xs" title={item.reason}>
                                  {item.model}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'statistics' && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gradient-to-br from-cyan-50 to-blue-50 rounded-lg p-4 border border-cyan-200">
                  <div className="text-sm font-medium text-gray-700 mb-1">Total Queries</div>
                  <div className="text-3xl font-bold text-cyan-600">
                    {datasource.total_queries || 0}
                  </div>
                </div>

                <div className="bg-gradient-to-br from-red-50 to-pink-50 rounded-lg p-4 border border-red-200">
                  <div className="text-sm font-medium text-gray-700 mb-1">Total Errors</div>
                  <div className="text-3xl font-bold text-red-600">
                    {datasource.total_errors || 0}
                  </div>
                </div>

                <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg p-4 border border-green-200">
                  <div className="text-sm font-medium text-gray-700 mb-1">Avg Response Time</div>
                  <div className="text-3xl font-bold text-green-600">
                    {datasource.avg_response_time_ms || 0}
                    <span className="text-lg ml-1">ms</span>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-purple-50 to-violet-50 rounded-lg p-4 border border-purple-200">
                  <div className="text-sm font-medium text-gray-700 mb-1">Success Rate</div>
                  <div className="text-3xl font-bold text-purple-600">
                    {datasource.total_queries
                      ? (
                          ((datasource.total_queries - (datasource.total_errors || 0)) /
                            datasource.total_queries) *
                          100
                        ).toFixed(1)
                      : 0}
                    <span className="text-lg ml-1">%</span>
                  </div>
                </div>
              </div>

              {datasource.total_queries === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <p>No queries yet. Statistics will appear once the datasource is used.</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-3 pt-6 border-t border-gray-200 mt-6">
          <button onClick={onDelete} className="btn btn-r">
            Delete source
          </button>
          <div className="flex-1"></div>
          <button onClick={onClose} className="btn btn-g">
            Close
          </button>
        </div>
      </div>
    </Modal>
  );
};
